import videojs from 'video.js';

import { v4 as uuid } from 'uuid';

const _activeCues = new WeakMap();
const _startCallbacks = {};
const _endCallbacks = {};

/**
 * @typedef {Object} TimedEvent
 * @alias TimedEvent
 * @memberof TimedEventsPlugin
 * @property {string} [id] An ID by which to identify the event.
 * @property {number} time The time in seconds.
 * @property {string} type The type of event. If the type is 'hidden', 'timedeventreached' events won't be fired on the player for this event.
 * @property {string} payload A string of arbitrary data to associate with the event for future reference.
 * @property {number} [duration] The duration in seconds.
 * @property {string} [marker] Causes a timeline marker to be created with the given label.
 */

/**
 * The TimedEvents module.
 * @member {TimedEventsPlugin} timedEvents
 * @memberof MediasitePlayer#
 */

/**
 * Enables registering callbacks that will fire at specific times during playback.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class TimedEventsPlugin extends videojs.getPlugin('plugin') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} [options]
     */
    constructor(player, options) {
        super(player, options);

        _activeCues.set(this, []);

        this.metadataTrack = new videojs.TextTrack({
            tech: player,
            kind: 'metadata',
            mode: 'hidden'
        });

        this.on(this.metadataTrack, 'cuechange', this.#cueChanged);
    }

    /**
     * 
     * @param {TimedEvent} timedEvent A timed event to register.
     * @param {TimedEventCallback} startCallback A function that will be called with the event's unique ID whenever the playback time enters the event's time span.
     * @param {TimedEventCallback} endCallback A function that will be called with the event's unique ID whenever the playback time exits the event's time span.
     * @example
     * const eventId = player.timedEvents.add(
     *     {
     *         time: 20,
     *         type: 'my-custom-type',
     *         payload: { custom: 'data' }
     *     },
     *     id => console.log(`Timed event callback for event with id ${id}`),
     *     id => console.log(`Timed event callback for event with id ${id}`)
     * );
     */
    add(timedEvent, startCallback, endCallback) {
        const { time, duration = 0, type, payload, marker } = timedEvent;
        const id = timedEvent.id ?? uuid();

        if (!Array.from(this.metadataTrack.cues).some(cue => cue.id === id)) {
            this.metadataTrack.addCue({
                startTime: time,
                endTime: time + duration,
                id,
                text: JSON.stringify({
                    type,
                    payload
                })
            });
            _startCallbacks[id] = () => {
                startCallback?.(id);
                this.#logStart(JSON.stringify({
                    type,
                    payload
                }));
            };            
            _endCallbacks[id] = () => {
                endCallback?.(id);
                this.#logEnd(JSON.stringify({
                    type,
                    payload
                }));
            }; 

            if (marker) {
                this.player.timelineMarkers().add({ id, time, label: marker });
            }

            return id;
        }
    }

    //Some logging methods to help determine if timed events occured
    #logStart(info) {
        this.log(`Started timed event ${info} at ${this.player.currentTime()}`);
    }

    #logEnd(info) {
        this.log(`Ended timed event ${info} at ${this.player.currentTime()}`);
    }

    /**
     * Removes a timed event (as well as its marker in the seek bar, if present).
     * @param {string} id The event's unique ID.
     * @example
     * player.timedEvents.remove('eventId');
     */
    remove(id) {
        for (let i = 0; i < this.metadataTrack.cues.length; i++) {
            const cue = this.metadataTrack.cues[i];
            if (cue.id === id) {
                this.metadataTrack.removeCue(cue);
                delete _startCallbacks[cue.id];
                delete _endCallbacks[cue.id];

                if (this.player.usingPlugin('timelineMarkers')) {
                    this.player.timelineMarkers().remove(cue.id);
                }

                break;
            }
        }
    }

    /**
     * Updates the timed event with the given ID.
     * @param {string} id
     * @param {TimedEvent} timedEvent
     * @example
     * player.timedEvents.update('eventId', { });
     */
    update(id, timedEvent) {
        const { time, duration, type, marker, payload = {} } = timedEvent;

        const cue = Array.from(this.metadataTrack.cues).find(cue => cue.id === id);
        if (cue) {
            if (typeof time === 'number') {
                cue.startTime = time;
            }
            if (typeof duration === 'number') {
                cue.endTime = cue.startTime + duration;
            }
        }

        const data = JSON.parse(cue.text);

        if (payload) {
            Object.assign(data, { payload });
        }
        if (type) {
            Object.assign(data, { type });
        }

        cue.text = JSON.stringify(data);

        this.player.timelineMarkers().update(id, { time, label: marker });
    }

    /**
     * Returns a list of TimedEvent objects with the given type.
     * @param {string} eventType The type of event to receive. If not passed, all events will be returned (excluding events of type 'hidden').
     * @returns {TimedEvent[]}
     * @example
     * const allEventsOfType = player.timedEvents.getAll('my-custom-type');
     */
    getAll(eventType) {
        return Array.from(this.metadataTrack.cues).reduce((all, current) => {
            try {
                const { type, payload } = JSON.parse(current.text);
                if ((eventType && eventType === type) || (!eventType && type !== 'hidden')) {
                    all.push({
                        id: current.id,
                        time: current.startTime,
                        type,
                        payload
                    });
                }
            }
            catch (e) {
                console.error(e);
            }
            return all;
        }, []);
    }

    /**
    * Returns the TimedEvent with the given ID.
    * @param {string} id
    * @returns {?TimedEvent}
    * @example
    * const timedEvent = player.timedEvents.get('eventId');
    */
    get(id) {
        const cue = Array.from(this.metadataTrack.cues).find(cue => cue.id === id);
        return cue ? {
            id: cue.id,
            time: cue.startTime,
            duration: cue.endTime - cue.startTime,
            ...JSON.parse(cue.text)
        } : null;
    }

    /**
     * @private
     */
    #cueChanged() {
        // Check active cues.
        const previousCues = _activeCues.get(this);
        const activeCues = Array.from(this.metadataTrack.activeCues);

        for (let i = 0; i < this.metadataTrack.cues.length; i++) {
            const cue = this.metadataTrack.cues[i];
            const id = cue.id;
            const isActive = activeCues.some(item => item.id === cue.id);
            const wasActive = previousCues.some(item => item.id === cue.id);

            let callback;
            if (isActive && !wasActive) {
                callback = _startCallbacks[id];

                const { startTime: time } = cue;
                const { type, payload } = JSON.parse(cue.text);
                if (type !== 'hidden') {
                    this.player.trigger('timedeventreached', {
                        id,
                        time,
                        type,
                        payload
                    });
                }
            }
            else if (wasActive && !isActive) {
                callback = _endCallbacks[id];
            }
            else {
                // No change for this cue
            }

            callback?.(id);
        }

        _activeCues.set(this, activeCues);
    }

    clear() {
        for (const { id } of Array.from(this.metadataTrack.cues)) {
            this.remove(id);
        }
    }

    dispose() {
        for (const { id } of Array.from(this.metadataTrack.cues)) {
            this.remove(id);
        }

        super.dispose();
    }
}

/**
 * A callback function that is invoked when the timed event occurs.
 * @callback TimedEventCallback
 * @alias TimedEventCallback
 * @memberof TimedEventsPlugin
 * @param {string} id The unique ID of the event that occurred.
 */

export default TimedEventsPlugin;
